import React, { FC, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import CreateCompany from './CreateCompany';
import Settings from './Settings';
import { ToastContainer } from 'react-toastify';
import StatusCheckWrapper from './misc/StatusCheckWrapper';
import ChatInterface from './chat/ChatInterface';
import ProDocView from './chat/ProDocView';
import ProDocList from './chat/ProDocList';
import DreamDaily from './chat/DreamDaily';
import MemoryArea from './chat/MemoryArea';
import { AgentProvider, useAgent } from '../contexts/AgentContext';
import { ReplyToProvider } from '../contexts/ReplyToContext';
import './chat/conversational-ui.scss';
import ConfirmAction from './ConfirmAction';
import UniPage from './chat/UniPage';
import BookingPage from './BookingPage';
import WebRTCVoiceChat from './WebRTCVoiceChat';
import HumanTasks from './chat/HumanTasks';
import InterventionOperatorPage from './ops/InterventionOperatorPage';
import SalesPlan from './SalesPlan';
import OrgTiers from './OrgTiers';
import { SalesInbox } from './chat/SalesInbox';
import AgentHomePage from './chat/AgentHomePage';
import TodayPage from './chat/TodayPage';
import FastChatButton from './common/FastChatButton';
import CallNotification from './common/CallNotification';
import { MobileNavProvider, useMobileNav } from '../contexts/MobileNavContext';
import MobileNavigation from './chat/MobileNavigation';
import SuggestionArchetypeEditor from './chat/SuggestionArchetypeEditor';
import SuggestionSimulation from './chat/SuggestionSimulation';
import PlatformStats from './chat/PlatformStats';
import AppBackfills from './chat/AppBackfills';
import HostedCheckoutArtifactResume from './chat/HostedCheckoutArtifactResume';
import AiCallTester from './admin/AiCallTester';
import OperatorRota from './admin/OperatorRota';
import OnboardingTesting from './admin/OnboardingTesting';
import NotFoundPage from './NotFoundPage';
import TodoListRedirect from './morning-list/TodoListRedirect';

const LayoutMobileNavigation: FC = () => {
  const { isMobile, isMobileNavOpen, closeMobileNav } = useMobileNav();
  if (!isMobile) return null;
  return <MobileNavigation isOpen={isMobileNavOpen} onClose={closeMobileNav} />;
};

/** On mobile, Memories lives under Settings; desktop keeps its own page. */
const MemoryRoute: FC = () => {
  const { isMobile } = useMobileNav();
  const location = useLocation();
  if (isMobile) {
    return <Navigate to='/settings/memories' replace state={location.state} />;
  }
  return <MemoryArea />;
};

const ConversationalUIContent: FC = () => {
  const { loadAgents } = useAgent();
  const location = useLocation();

  const isVoiceChat = location.pathname === '/voice-chat';
  const isDreamDaily = location.pathname === '/dream-daily';
  const isHumanTasks =
    location.pathname === '/tasks' ||
    location.pathname.startsWith('/ops/intervention/');
  const isBaseRoute = location.pathname.indexOf('/docs') !== -1;

  // Detect standalone PWA mode and add CSS class (iOS doesn't always match display-mode: standalone)
  useEffect(() => {
    const isStandalone =
      (window.navigator as any).standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) {
      document.documentElement.classList.add('pwa-standalone');
    }
  }, []);

  // Load agents on component mount
  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  return (
    <div className='conversational-outer'>
      <div
        className={`conversational-container ${isVoiceChat || isDreamDaily || isHumanTasks ? 'no-nav' : ''} ${isBaseRoute ? 'high-af' : ''}`}
      >
        {/* {!isVoiceChat && <NotificationPermissionBanner />} */}
        <div className={`conversational-content`}>
          <Routes>
            <Route path='/' element={<UniPage />} />
            {/* Retired Task Manager URL, still live in old links and messages */}
            <Route path='/todos' element={<Navigate to='/' replace />} />
            {/* First-class shared-list link (usedream.ai/to-do-list/<id>) */}
            <Route path='/to-do-list/:listId' element={<TodoListRedirect />} />
            <Route path='/conversations' element={<ChatInterface />} />
            <Route path='/memory' element={<MemoryRoute />} />
            <Route path='/docs' element={<ProDocList />} />
            <Route path='/docs/:proDocId' element={<ProDocView />} />
            <Route path='/dream-daily' element={<DreamDaily />} />
            <Route path='/tasks' element={<HumanTasks />} />
            <Route
              path='/ops/intervention/:commandId'
              element={<InterventionOperatorPage />}
            />
            <Route path='/sales-plan' element={<SalesPlan />} />
            <Route path='/sales-inbox' element={<SalesInbox />} />
            <Route path='/agent-home' element={<AgentHomePage />} />
            <Route path='/today' element={<TodayPage />} />
            <Route path='/booking/:key' element={<BookingPage />} />

            <Route
              path='/confirm/:confirmationCode'
              element={<ConfirmAction />}
            />
            <Route path='/create-company' element={<CreateCompany />} />
            <Route path='/settings' element={<Settings />} />
            <Route path='/settings/:setting' element={<Settings />} />
            <Route path='/voice-chat' element={<WebRTCVoiceChat />} />
            <Route path='/org-tiers' element={<OrgTiers />} />
            <Route
              path='/suggestion-archetypes'
              element={<SuggestionArchetypeEditor />}
            />
            <Route
              path='/suggestion-simulation'
              element={<SuggestionSimulation />}
            />
            <Route path='/platform-stats/*' element={<PlatformStats />} />
            <Route path='/app-backfills' element={<AppBackfills />} />
            <Route path='/ai-call-test' element={<AiCallTester />} />
            <Route path='/operator-rota' element={<OperatorRota />} />
            <Route path='/onboarding-testing' element={<OnboardingTesting />} />
            {/* Add any other routes that should be accessible in conversational mode */}
            <Route path='*' element={<NotFoundPage />} />
          </Routes>
        </div>
        <ToastContainer className='toast-container-wrapper' />
        <StatusCheckWrapper />
        {/* <FastChatButton /> */}
        <CallNotification />
        <HostedCheckoutArtifactResume />

        <LayoutMobileNavigation />
      </div>
    </div>
  );
};

const ConversationalUI: FC = () => {
  return (
    <AgentProvider>
      <ReplyToProvider>
        <MobileNavProvider>
          <ConversationalUIContent />
        </MobileNavProvider>
      </ReplyToProvider>
    </AgentProvider>
  );
};

export default ConversationalUI;
